import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import type { ApiError } from '../../api/apiError';
import { layout } from '../../constants/layout';
import { routes, type AuthStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  requestPasswordReset,
  resetForgottenPassword,
  verifyPasswordResetOtp,
} from '../../services/authService';
import { validateNewPassword } from './ChangePasswordScreen';

type Props = NativeStackScreenProps<AuthStackParamList, 'ForgotPassword'>;
type Step = 'request' | 'verify' | 'reset' | 'done';

/** Codes that mean the user must start again with a new verification code. */
const restartCodes = new Set(['RESET_TOKEN_INVALID', 'RESET_TOKEN_EXPIRED']);

const resetMessages: Record<string, string> = {
  PASSWORD_RESET_UNAVAILABLE:
    'Password reset by e-mail is not available right now. Contact your HR administrator.',
  OTP_DELIVERY_FAILED:
    'The verification code could not be sent. Please try again later.',
  OTP_INVALID: 'The verification code is invalid or has expired. Request a new code.',
  OTP_EXPIRED: 'The verification code has expired. Request a new code.',
  OTP_INCORRECT: 'The verification code is incorrect.',
  RESET_TOKEN_INVALID:
    'This password reset session is no longer valid. Request a new code.',
  RESET_TOKEN_EXPIRED:
    'This password reset session has expired. Request a new code.',
  PASSWORD_REUSED: 'Choose a password you have not used recently.',
  PASSWORD_RESET_NOT_ALLOWED: 'Password reset is not available for this account.',
  RATE_LIMIT_EXCEEDED: 'Too many attempts. Please wait a few minutes and try again.',
  NETWORK_ERROR: 'Unable to connect to the server.',
  REQUEST_TIMEOUT: 'The request timed out. Please try again.',
};

function resetErrorMessage(error: unknown): string {
  const apiError = error as ApiError;
  if (apiError?.code && resetMessages[apiError.code]) return resetMessages[apiError.code];
  if (apiError?.code === 'VALIDATION_ERROR' && apiError.message) return apiError.message;
  if (typeof apiError?.message === 'string' && apiError.message.trim()) {
    return apiError.message;
  }
  return 'Unable to reset your password. Please try again.';
}

export function ForgotPasswordScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const [step, setStep] = useState<Step>('request');
  const [loginName, setLoginName] = useState(route.params?.loginName ?? '');
  const [otp, setOtp] = useState('');
  const [otpLength, setOtpLength] = useState<number | null>(null);
  const [otpExpiresInMinutes, setOtpExpiresInMinutes] = useState<number | null>(null);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = setTimeout(() => setResendSeconds(value => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendSeconds]);

  const themed = useMemo(
    () => ({
      brand: {
        ...typography.screenTitle,
        color: palette.white,
        fontWeight: '700' as const,
      },
      title: { ...typography.sectionTitle, color: palette.textPrimary },
      subtitle: { ...typography.body, color: palette.textSecondary },
      error: { ...typography.caption, color: palette.error, textAlign: 'center' as const },
      notice: { ...typography.caption, color: palette.success, textAlign: 'center' as const },
      hint: { ...typography.caption, color: palette.textSecondary },
    }),
    [palette],
  );

  function resetMessagesState() {
    setFieldError('');
    setError('');
    setNotice('');
  }

  function startOver(message: string) {
    setStep('request');
    setOtp('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
    setResendSeconds(0);
    setError(message);
  }

  async function sendCode(isResend: boolean) {
    if (submitting) return;
    resetMessagesState();
    const normalizedLoginName = loginName.trim();
    if (!normalizedLoginName) {
      setFieldError('Enter your username or e-mail address.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await requestPasswordReset({ loginName: normalizedLoginName });
      setLoginName(normalizedLoginName);
      setOtpLength(result.otpLength);
      setOtpExpiresInMinutes(result.otpExpiresInMinutes);
      setResendSeconds(result.resendAvailableInSeconds);
      setOtp('');
      setStep('verify');
      setNotice(
        isResend
          ? 'If the account is registered, a new code has been sent.'
          : 'If an active account matches these details, a verification code has been sent to its registered e-mail address.',
      );
    } catch (caught) {
      setError(resetErrorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function verifyCode() {
    if (submitting) return;
    resetMessagesState();
    const code = otp.trim();
    if (!code) {
      setFieldError('Enter the verification code.');
      return;
    }
    if (otpLength && !new RegExp(`^\\d{${otpLength}}$`).test(code)) {
      setFieldError(`The verification code must be ${otpLength} digits.`);
      return;
    }
    setSubmitting(true);
    try {
      const result = await verifyPasswordResetOtp({ loginName, otp: code });
      setResetToken(result.resetToken);
      setOtp('');
      setStep('reset');
    } catch (caught) {
      // The code is no longer usable, so a new one can be requested right away.
      if (['OTP_INVALID', 'OTP_EXPIRED'].includes((caught as ApiError)?.code ?? '')) {
        setResendSeconds(0);
      }
      setError(resetErrorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function savePassword() {
    if (submitting) return;
    resetMessagesState();
    const validation = validateNewPassword(newPassword);
    if (validation) return setFieldError(validation);
    if (newPassword !== confirmPassword) return setFieldError('Passwords do not match.');
    setSubmitting(true);
    try {
      await resetForgottenPassword({ resetToken, newPassword, confirmPassword });
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setStep('done');
    } catch (caught) {
      const code = (caught as ApiError)?.code;
      if (code && restartCodes.has(code)) {
        startOver(resetErrorMessage(caught));
      } else {
        setError(resetErrorMessage(caught));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function backToLogin() {
    navigation.navigate(routes.login);
  }

  const heading = {
    request: 'Forgot Password',
    verify: 'Enter Verification Code',
    reset: 'Set a New Password',
    done: 'Password Reset',
  }[step];

  return (
    <AppScreen
      scroll
      keyboardSafe
      webLayout="fluid"
      contentStyle={styles.screen}
      backgroundColor={palette.background}
    >
      <View style={[styles.hero, { backgroundColor: palette.brand }]}>
        <Pressable
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : backToLogin())}
          accessibilityRole="button"
          accessibilityLabel="Back"
          hitSlop={10}
          style={({ pressed }) => [
            styles.backButton,
            {
              backgroundColor: pressed
                ? 'rgba(255,255,255,0.22)'
                : 'rgba(255,255,255,0.14)',
            },
          ]}
        >
          <Text style={styles.backChevron}>‹</Text>
        </Pressable>
        <Text style={themed.brand}>{heading}</Text>
      </View>

      <View
        style={[
          styles.formArea,
          { maxWidth: layout.webFormMaxWidth, width: '100%', alignSelf: 'center' },
        ]}
      >
        {step === 'request' ? (
          <Text style={themed.subtitle}>
            Enter the username or e-mail address you sign in with. We will e-mail a
            verification code to the address registered on your account.
          </Text>
        ) : null}
        {step === 'verify' ? (
          <Text style={themed.subtitle}>
            Enter the code sent to the e-mail address registered for {loginName}.
            {otpExpiresInMinutes ? ` It expires in ${otpExpiresInMinutes} minutes.` : ''}
          </Text>
        ) : null}
        {step === 'reset' ? (
          <Text style={themed.subtitle}>
            Choose a new password. You will be signed out of all devices.
          </Text>
        ) : null}

        <AppCard style={styles.card}>
          {step === 'request' ? (
            <>
              <AppInput
                label="Email or username"
                value={loginName}
                onChangeText={value => {
                  setLoginName(value);
                  if (fieldError) setFieldError('');
                }}
                placeholder="Enter your email or username"
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="username"
                error={fieldError}
              />
              <AppButton
                title="Send code"
                onPress={() => sendCode(false)}
                loading={submitting}
              />
            </>
          ) : null}

          {step === 'verify' ? (
            <>
              <AppInput
                label="Verification code"
                value={otp}
                onChangeText={value => {
                  setOtp(value.replace(/\D/g, ''));
                  if (fieldError) setFieldError('');
                }}
                placeholder={otpLength ? `${otpLength}-digit code` : 'Verification code'}
                keyboardType="number-pad"
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="oneTimeCode"
                error={fieldError}
              />
              <AppButton title="Verify code" onPress={verifyCode} loading={submitting} />
              <AppButton
                title={resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend code'}
                variant="text"
                onPress={() => sendCode(true)}
                disabled={submitting || resendSeconds > 0}
              />
              <AppButton
                title="Use a different account"
                variant="text"
                onPress={() => startOver('')}
                disabled={submitting}
              />
            </>
          ) : null}

          {step === 'reset' ? (
            <>
              <AppInput
                label="New password"
                value={newPassword}
                onChangeText={setNewPassword}
                secureTextEntry
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="newPassword"
              />
              <AppInput
                label="Confirm new password"
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                secureTextEntry
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="newPassword"
              />
              <Text style={themed.hint}>
                Use 8 to 128 characters with uppercase, lowercase, number, and special
                character.
              </Text>
              {fieldError ? (
                <Text accessibilityRole="alert" style={themed.error}>
                  {fieldError}
                </Text>
              ) : null}
              <AppButton title="Reset password" onPress={savePassword} loading={submitting} />
            </>
          ) : null}

          {step === 'done' ? (
            <>
              <Text accessibilityRole="alert" style={themed.notice}>
                Your password has been reset. Sign in with your new password.
              </Text>
              <AppButton title="Back to sign in" onPress={backToLogin} />
            </>
          ) : null}

          {notice ? (
            <Text accessibilityRole="alert" style={themed.notice}>
              {notice}
            </Text>
          ) : null}
          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}
        </AppCard>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  screen: { paddingHorizontal: 0, paddingVertical: 0 },
  hero: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xxl,
    paddingBottom: spacing.xxl,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
    gap: spacing.sm,
  },
  backButton: {
    alignSelf: 'flex-start',
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  backChevron: {
    color: '#FFFFFF',
    fontSize: 32,
    lineHeight: 34,
    fontWeight: '300',
    marginTop: -2,
  },
  formArea: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    gap: spacing.sm,
  },
  card: { gap: spacing.lg, marginTop: spacing.md, maxWidth: '100%', minWidth: 0 },
});
